import { useEffect, useState, useMemo } from "react";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { getSecondarySchemesColumns } from "../utils/utils";
import {
  setClaimSummaryRows,
  setSecondarySchemesRows,
  setInitialSecondarySchemesRows,
  setSelectedClaimSubType,
  setSelectedClaimType,
  setSecondarySchemesCheckedClaimIds,
  setMonth,
  setSearchText,
  setNonEligible
} from "../../../stateManagement/actions/claims";
import { useSelector, useDispatch } from "react-redux";
import { getFormattedDateRange, getCurrentDateTime, getLast12Months } from "../../../utils/calenderUtils";
import { getExclTovInvoices, getClaims } from "../../../services/claimService";
import { getConfigData } from "../../../services/configurationService";
import { getDataFromLocalDb } from "../../../services/dbService";
import { Loader } from "../../../components/loader/Loader";
import MiniClaimSummary from "../components/MiniClaimSummary";
import "./PurchaseReturns.css";
import { logLoadTrace } from "../../../../src/firebase";

const SecondarySchemes: React.FC = () => {
  const { 
    secondarySchemesRows, 
    initialSecondarySchemesRows, 
    secondarySchemesCheckedClaimIds, 
    month, 
    searchText,
    nonEligible 
  } = useSelector((state: any) => state.claims);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { batchIdConfiguration, otherUnitLabel, displayOtherUnitName } = ConfigDictionary;
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);

  const filteredRows = useMemo(() => {
    const term = searchText?.toLowerCase().replace(/\s/g, '') || '';
    if (!term) {
      return secondarySchemesRows.map((row:any) => ({
        ...row,
        isNonEligible: nonEligible.includes(row.skuCode)
      }));
    }

    return secondarySchemesRows
    .map((row: any) => ({
      ...row,
      isNonEligible: nonEligible.includes(row.skuCode)
    }))
    .filter((row: any) => {
      const matches = [row.skuCode, row.skuName, row.schemeType]
        .some(field => field?.toLowerCase().replace(/\s/g, '').includes(term));
      return matches;
    });
}, [searchText, secondarySchemesRows, nonEligible]);
  
  const visibleCheckedIds = useMemo(() => {
    return secondarySchemesCheckedClaimIds.filter((id:any) => 
      filteredRows.some((row:any) => row.id === id)
  )}, [secondarySchemesCheckedClaimIds, filteredRows]);

  const fetchData = async () => {
    if (!month) return;
    setLoader(true);
    try {
      const [formattedStartDate, formattedEndDate] = getFormattedDateRange(month);
      if (!formattedStartDate || !formattedEndDate) return;
  
      let products: Record<string, any> = {};
      if (displayOtherUnitName) {
        const dbResult = await getDataFromLocalDb("Product");
        products = dbResult?.data?.[0]?.data ?? {};
      }

      const [exclTovInvoicesResponse, claimsResponse, secondarySchemesDiscountInfoKeyMapping] = await Promise.allSettled([
        getExclTovInvoices(formattedStartDate.replace("%", " "), formattedEndDate.replace("%", " ")),
        getClaims(formattedStartDate.replace("%", "T"), getCurrentDateTime(new Date(), true).replace("%", "T"), "AllExclTOV"),
        getConfigData("dms_configuration", "secondarySchemesDiscountInfoKeyMapping"),
      ]);
      
      const exclTovInvoicesData = exclTovInvoicesResponse.status === "fulfilled" ? exclTovInvoicesResponse.value : [];
      const claimsData = claimsResponse.status === "fulfilled" ? claimsResponse.value : [];
      
      const secondarySchemesDiscountInfoKey = secondarySchemesDiscountInfoKeyMapping.status === "fulfilled" ? secondarySchemesDiscountInfoKeyMapping.value[0]?.configValue : {
        "discount": "discount",
        "discountId": "discountId",
        "discountInfo": "discountInfo",
        "discountType": "discountType",
        "discountBatchCode": "discountBatchCode"
      };
  
      const claimedInvoiceMap: Record<string, { status: string, approvedAmount: number }> = {};
      
      claimsData.forEach((claim: any) => {
        claim.claimDetails.forEach((detail: any) => {
          const key = batchIdConfiguration 
          ? `${detail.skuCode}-${detail.referenceNumber}-${detail.remark || 'Free Of Cost'}-${detail.batchCode || ''}`
          : `${detail.skuCode}-${detail.referenceNumber}-${detail.remark || 'Free Of Cost'}`;
          
          if (claim.status === 'APPROVED' || 
              claim.status === 'REQUESTED' ||
              (claim.status === 'PARTIALLY_APPROVED' && detail.approvedAmount > 0)) {
            claimedInvoiceMap[key] = {
              status: claim.status,
              approvedAmount: detail.approvedAmount
            };
          }
        });
      });
  
      const existingModifiedRowsMap: Record<string, any> = {};
      (secondarySchemesRows || [])
        .filter((row: any) => row.id.startsWith(month))
        .forEach((row: any) => {
          const key = `${row.skuCode}-${row.schemeType}`;
          existingModifiedRowsMap[key] = row;
        });
  
      const schemeTypeMap: { [key: string]: any } = {};
  
      exclTovInvoicesData.forEach((invoice: any) => {
        if (!invoice.discount_info) return;
        
        const skuCode = invoice.skucode;
        const invoiceNumber = invoice.inv_ser_no ? invoice.inv_ser_no : invoice.invoice_number;
        const batchIds = invoice.batch_id || '';
        let customOtherUnitLabel = otherUnitLabel;

        if (displayOtherUnitName) {
          const product = products[skuCode];
          if (product?.otherUnitName && product.otherUnitName.length > 0) {
            customOtherUnitLabel = product.otherUnitName;
          }
        }
        const discountsBySchemeType: Record<string, any[]> = {};
        const validDiscounts = invoice.discount_info.filter((d: any) => d[secondarySchemesDiscountInfoKey.discountInfo]);
        validDiscounts.forEach((discount: any) => {
          const schemeType = discount[secondarySchemesDiscountInfoKey.discountInfo] || "Free Of Cost";
          if (!discountsBySchemeType[schemeType]) {
            discountsBySchemeType[schemeType] = [];
          }
          discountsBySchemeType[schemeType].push(discount);
        });
      
        Object.entries(discountsBySchemeType).forEach(([schemeType, schemeDiscounts]) => {
          const totalDiscount = schemeDiscounts.reduce((sum: number, discount: any) => {
            return sum + (Number(discount[secondarySchemesDiscountInfoKey.discount]) || 0);
          }, 0);
                
          const key = `${skuCode}-${schemeType}`;    
          const invoiceKey = batchIdConfiguration 
          ? `${skuCode}-${invoiceNumber}-${schemeType}-${batchIds}`
          : `${skuCode}-${invoiceNumber}-${schemeType}`;

          if (claimedInvoiceMap[invoiceKey] && 
              (claimedInvoiceMap[invoiceKey].status === 'REQUESTED' ||
                claimedInvoiceMap[invoiceKey].status === 'APPROVED' || 
               (claimedInvoiceMap[invoiceKey].status === 'PARTIALLY_APPROVED' && 
                claimedInvoiceMap[invoiceKey].approvedAmount > 0))) {
            return;
          }
  
          if (existingModifiedRowsMap[key]) {
            schemeTypeMap[key] = { ...existingModifiedRowsMap[key] };
            return;
          }
      
          if (!schemeTypeMap[key]) {
            schemeTypeMap[key] = {
              skuCode,
              skuName: invoice.product_info?.skuDescription || "N/A",
              invoices: [],
              schemeType: schemeType,
              cases: 0,
              others: 0,
              pieces: 0,
              totalAmount: 0,
              discount: 0,
              finalAmount: 0,
              totalFoc: 0,
              id: `${month}-${Object.keys(schemeTypeMap).length + 1}`,
              otherUnitName: customOtherUnitLabel
            };
          }
      
          const uniqueInvoiceId = batchIdConfiguration 
          ? `${skuCode}-${invoiceNumber}-${schemeType}-${batchIds}`
          : `${skuCode}-${invoiceNumber}-${schemeType}`;
          const existingInvoice = schemeTypeMap[key].invoices.find(
            (inv: any) => inv.id === uniqueInvoiceId
          );
      
          if (!existingInvoice) {
            const focCount = schemeDiscounts.filter((discount: any) =>  discount[secondarySchemesDiscountInfoKey.discountType] === "item" &&  !!discount[secondarySchemesDiscountInfoKey.discountBatchCode]).length;
            
            const discountInfo = schemeDiscounts.map((info: any) => ({
              promoId: info?.promoId || "",
              promoDescription: info?.promoDescription || "",
              startRange: info?.startRange ?? 0,
              endRange: info?.endRange ?? 0,
              benefit: info?.benefitPercentage ?? info?.finalBenefit ?? 0,
              rangeUnit: info?.rangeUnit || "",
              promoType: info?.promoType,
              evaluator: info?.evaluator,
              priority: info?.priority ?? 0,
              promoAttribute: info?.promoAttribute ?? [],
            }));
            const invoiceObj = {
              id: uniqueInvoiceId,
              invoiceNumber: invoiceNumber,
              invoiceDate: new Date(invoice.creation_time).toLocaleDateString("en-GB").replace(/\//g, "-"),
              outletName: invoice.outletName || '',
              outletCode: invoice.outletcode || '',
              blobKey: invoice.extended_attributes?.blobKey,
              discountsAndPromotions: [schemeType],
              cases: invoice.initial_case_quantity,
              others: invoice.initial_other_unit_quantity,
              pieces: invoice.initial_piece_quantity,
              pricePerCase: invoice.case_price,
              pricePerPiece: invoice.price,
              amount: invoice.total_amount,
              discount: totalDiscount,
              finalAmount: totalDiscount,
              foc: focCount,
              batchCode: invoice.batch_code || '',
              batchIds: batchIdConfiguration ? batchIds : '',
              schemeType: schemeType,
              discountInfo: discountInfo,
              otherUnitName: customOtherUnitLabel
            };
      
            schemeTypeMap[key].invoices.push(invoiceObj);
            schemeTypeMap[key].cases += invoice.initial_case_quantity;
            schemeTypeMap[key].pieces += invoice.initial_piece_quantity;
            schemeTypeMap[key].others += invoice.initial_other_unit_quantity;
            schemeTypeMap[key].totalAmount += invoice.total_amount;
            schemeTypeMap[key].discount += totalDiscount;
            schemeTypeMap[key].finalAmount += totalDiscount;
            schemeTypeMap[key].totalFoc += focCount;
          }
        });
      });
  
      const rows = Object.values(schemeTypeMap)
        .filter((row: any) => row.cases !== 0 || row.others !== 0 || row.pieces !== 0)
        .map((row: any) => ({
          ...row,
          invoiceNo: row.invoices.length,
        }));
  
      dispatch(setInitialSecondarySchemesRows(rows || []));
      dispatch(setSecondarySchemesRows(rows || []));
  
      const previousCheckedIds = secondarySchemesCheckedClaimIds;
      const filteredCheckedIds = previousCheckedIds.filter((id: any) =>
        rows.some(row => row.id === id)
      );
      
      dispatch(setSecondarySchemesCheckedClaimIds(
        filteredCheckedIds.length ? filteredCheckedIds :
          rows.filter(row => row.totalAmount !== 0).map(row => row.id)
      ));
  
    } catch (error) {
      console.error("Error fetching TOV invoices:", error);
    } finally {
      setLoader(false);
    }
  };

  const init = async ()=> {
    setLoader(true);
    await Promise.allSettled([fetchData()]).finally(
      () => {
        setLoader(false);
      }
    )
  }

  useEffect(() => {
    if (month) {
    logLoadTrace(init, "claims_all_excl_tov_secondary_schemes_page_load_time");
    }
  },[month]);

  useEffect(() => {
    const updatedClaimSummaryRows = secondarySchemesRows.filter((obj: any) =>
      secondarySchemesCheckedClaimIds.includes(obj.id)
    );
    dispatch(setClaimSummaryRows(updatedClaimSummaryRows));
  }, [secondarySchemesCheckedClaimIds, secondarySchemesRows]);

  useEffect(() => {
    dispatch(setSelectedClaimType("Secondary Schemes"));
    dispatch(setSelectedClaimSubType("All Excl. Tov"));
  }, []);

  const handleSetCheckedIdArray = (ids: any[]) => {
    const validIds = ids.filter((id) => {
      const row = secondarySchemesRows.find((row: any) => row.id === id);
      return row && row.finalAmount !== 0;
    });
    dispatch(setSecondarySchemesCheckedClaimIds(validIds));
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('secondarySchemes')) {
        dispatch(setSearchText(""));
        dispatch(setSecondarySchemesRows(initialSecondarySchemesRows || []));
        dispatch(setSecondarySchemesCheckedClaimIds([]));
        const currentMonth = getLast12Months()[0];
        dispatch(setMonth(currentMonth));
        dispatch(setSelectedClaimSubType(""));
        dispatch(setNonEligible([]));
      }
    };
  }, []);

  return (
    <div className="claims_table_container">
      {loader ? (
        <Loader />
      ) : (
        <>
          <MiniClaimSummary rows={secondarySchemesRows} />
          <CustomDataGrid
            columnsMetaData={getSecondarySchemesColumns(nonEligible.length > 0)}
            rows={filteredRows}
            isCheckboxSelection={true}
            insideSecondarySchemes={true}
            tableWidth={0.794}
            checkedIdArray={visibleCheckedIds}
            setCheckedIdArray={handleSetCheckedIdArray}
          />
        </>
      )}
    </div>
  );
};

export default SecondarySchemes;